// Copied byte for byte into admin-panel/src/ui/hooks, which shares no package
// with client; client's useManagedPolling.mirror.spec.ts fails on any drift.
import { useEffect, useRef } from 'react';
import { useTabActivity } from './useTabActivity';

const POLL_INTERVAL = 1000;

interface PollingSchedule {
  pollInterval?: number;
  /** False pauses the poll, e.g. while logged out or off the view needing it. */
  enabled?: boolean;
}

/** Apollo runs the ticks; `refetch` serves the catch-up after a hidden tab. */
interface QueryPolling extends PollingSchedule {
  startPolling: (pollInterval: number) => void;
  stopPolling: () => void;
  refetch: () => Promise<unknown>;
  poll?: never;
}

/** The hook calls `poll` on every tick; `poll` handles its own errors. */
interface CallbackPolling extends PollingSchedule {
  poll: () => void;
  startPolling?: never;
  stopPolling?: never;
  refetch?: never;
}

export type UseManagedPollingOptions = QueryPolling | CallbackPolling;

// A tab back from a hidden stretch of a full interval refreshes at once in
// place of the skipped tick, so pausing never raises the request rate.
export const useManagedPolling = ({
  startPolling,
  stopPolling,
  refetch,
  poll,
  pollInterval = POLL_INTERVAL,
  enabled = true,
}: UseManagedPollingOptions) => {
  const isTabActive = useTabActivity();
  const hiddenSince = useRef<number | null>(null);

  useEffect(() => {
    if (!isTabActive) {
      hiddenSince.current ??= Date.now();
      return undefined;
    }
    const skippedATick =
      hiddenSince.current !== null &&
      Date.now() - hiddenSince.current >= pollInterval;
    hiddenSince.current = null;
    if (!enabled) {
      return undefined;
    }

    // A failed refetch is already reported by the Apollo error link.
    const refresh = poll ?? (() => refetch?.().catch(() => undefined));
    if (skippedATick) {
      refresh();
    }
    if (startPolling && stopPolling) {
      startPolling(pollInterval);
      return stopPolling;
    }
    const timer = setInterval(refresh, pollInterval);
    return () => clearInterval(timer);
  }, [
    isTabActive,
    enabled,
    pollInterval,
    startPolling,
    stopPolling,
    refetch,
    poll,
  ]);
};
